import { useMemo, useRef } from "react";
import useDataCore from "./hooks/useDataCore";
import PaginationUI from "./components/PaginationUI";
import InfinityUI from "./components/InfinityUI";
import { NoData } from "components/table/constants/NoData";

/**
 * Custom hook for managing paginated data.
 *
 * @param {Object} params
 * @param {string} params.pageName - Unique identifier for this page's data
 * @param {Function} params.fetchFn - Function to fetch data from API or source
 * @param {Function} params.deleteFn - Function to delete a row/item
 * @param {number} params.defaultLimit - Items per page (default is 10)
 *
 * @returns {Object} - Paginated data, utility functions, and pagination UI component
 */
const usePaginationCore = ({
  pageName,
  fetchFn,
  deleteFn,
  restoreFn,
  defaultLimit = 10,
  defaultParams = null,
  isCallFetchFn = true,
}) => {
  // useDataCore handles the core data operations: fetching, deleting, refreshing, and storing data
  const dataCore = useDataCore({
    pageName,
    fetchFn,
    deleteFn,
    restoreFn,
    append: false,
    limit: defaultLimit,
    defaultParams,
    isCallFetchFn,
  });

  const { isLoading, count, isFetchApi } = dataCore;

  const setLoaderRef = useRef();

  const tableFooter = useMemo(
    () => <PaginationUI count={Math.ceil(count / defaultLimit)} />,
    [count, defaultLimit],
  );

  const content = useMemo(() => {
    if (!isCallFetchFn || isLoading || !isFetchApi) return null;

    if (count === 0) {
      return <NoData />;
    }

    return null;
  }, [isLoading, isFetchApi, count, isCallFetchFn]);

  // Return everything needed for a component to manage and display paginated data
  return {
    ...dataCore,
    isShowNoData: !!content,
    paginationUI: (
      <InfinityUI
        content={content}
        isTableView={true}
        setLoaderRef={setLoaderRef}
      />
    ),
    tableFooter,
  };
};

export default usePaginationCore;
